import { ApiClient } from "../ApiClient";

const FEED_ENDPOINT = "feed";

/**
 * Fetch the authenticated user's feed timeline items
 * @param {number} page
 * @returns {Promise<Object>} API response with list of feed items
 */
export const fetchUserFeed = async (page = 1) => {
  try {
    const res = await ApiClient.get(`${FEED_ENDPOINT}/user-feed?page=${page}`);
    if (res.ok) {
      const result = await res.json();
      return {
        success: true,
        data: result.results || [],
        pagination: {
          timeline_id: result.timeline_id,
          page: result.page,
          next_page: result.next_page,
          has_next: result.has_next,
          total_items: result.total_items,
        },
        status: res.status,
      };
    }
    return {
      success: false,
      message: "Failed to fetch user feed",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred fetching user feed",
    };
  }
};

/**
 * Update reaction for a financial news item
 * @param {string} newsUuid - UUID of the financial news item
 * @param {number} voteType - Reaction choice: 1 (Like), -1 (Dislike), 0 (Love)
 * @param {string} type - Action type, defaults to 'vote'
 * @returns {Promise<Object>} Updated reaction counts
 */
export const updateNewsReaction = async (newsUuid, voteType, type = "vote") => {
  try {
    const res = await ApiClient.put(
      `${FEED_ENDPOINT}/financial-news/${newsUuid}/`,
      {
        type,
        vote_type: voteType,
      },
    );

    if (res.ok) {
      const data = await res.json();
      return {
        success: true,
        data, // { uuid, likes, dislikes, love }
      };
    } else {
      return {
        success: false,
        status: res.status,
      };
    }
  } catch (error) {
    return {
      success: false,
      error,
    };
  }
};

/**
 * Record user response or skip action for a feed interest card
 * @param {Object} payload - { interest_uuid, selected_answer, is_skipped }
 * @returns {Promise<Object>} API response status
 */
export const recordInterestResponse = async ({
  interest_uuid,
  selected_answer = null,
  is_skipped = false,
}) => {
  try {
    const res = await ApiClient.post(`${FEED_ENDPOINT}/user-feed/interest`, {
      interest_uuid,
      selected_answer,
      is_skipped,
    });

    if (res.ok) {
      const data = await res.json();
      return { success: true, data };
    }
    return {
      success: false,
      message: "Failed to record interest response",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.message || "An error occurred submitting interest response",
    };
  }
};

/**
 * Fetch comments for a financial news item
 * @param {string} newsUuid - UUID of the financial news item
 * @returns {Promise<Object>} List of comments
 */
export const fetchNewsComments = async (newsUuid) => {
  try {
    const res = await ApiClient.get(
      `${FEED_ENDPOINT}/financial-news/${newsUuid}/comments/`,
    );
    if (res.ok) {
      const data = await res.json();
      return {
        success: true,
        data: data.results || data || [],
      };
    }
    return {
      success: false,
      message: "Failed to fetch comments",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred fetching comments",
    };
  }
};

/**
 * Post a new comment on a financial news item
 * @param {string} newsUuid - UUID of the financial news item
 * @param {string} description - Comment text content
 * @returns {Promise<Object>} Newly created comment object
 */
export const createNewsComment = async (newsUuid, description) => {
  try {
    const res = await ApiClient.post(
      `${FEED_ENDPOINT}/financial-news/${newsUuid}/comments/`,
      { description },
    );
    if (res.ok) {
      const data = await res.json();
      return {
        success: true,
        data,
      };
    }
    return {
      success: false,
      message: "Failed to post comment",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred posting comment",
    };
  }
};

/**
 * Update reaction (vote) for a news comment
 * @param {string} commentUuid - UUID of the comment
 * @param {number} voteType - Reaction choice: 1 (Like), 2 (Dislike)
 * @returns {Promise<Object>} Updated comment vote object
 */
export const updateCommentReaction = async (commentUuid, voteType) => {
  try {
    const res = await ApiClient.put(
      `${FEED_ENDPOINT}/financial-news/comments/${commentUuid}/`,
      { vote_type: voteType },
    );

    if (res.ok) {
      const data = await res.json();
      return {
        success: true,
        data, // { uuid, up_votes, down_votes, user_vote }
      };
    }
    return {
      success: false,
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      error,
    };
  }
};

/**
 * Fetch filtered financial news (e.g. stocks or crypto)
 * @param {Object} params - { news_type: 'stocks' | 'crypto', page: number, limit: number }
 * @returns {Promise<Object>}
 */
export const fetchFinancialNews = async ({
  news_type,
  page = 1,
  limit = 10,
}) => {
  try {
    const queryParams = new URLSearchParams();
    if (news_type) queryParams.append("news_type", news_type);
    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);

    const res = await ApiClient.get(
      `${FEED_ENDPOINT}/financial-news?${queryParams.toString()}`,
    );

    if (res.ok) {
      const result = await res.json();
      return {
        success: true,
        data: result.data || [],
        pagination: result.pagination || {
          has_next: false,
          page: 1,
          total: 0,
        },
        status: res.status,
      };
    }
    return {
      success: false,
      message: "Failed to fetch financial news",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred fetching financial news",
    };
  }
};

/**
 * Fetch trending financial news for the carousel or full list view
 * @param {number} limit - Number of trending news items (default 10)
 * @returns {Promise<Object>} API response with list of trending items
 */
export const fetchTrendingNews = async (limit = 10) => {
  try {
    const res = await ApiClient.get(
      `${FEED_ENDPOINT}/financial-news/trending?limit=${limit}`,
    );

    if (res.ok) {
      const result = await res.json();
      return {
        success: true,
        data: (result.data || []).map((item) => item.content || item),
      };
    }
    return {
      success: false,
      message: "Failed to fetch trending news",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred fetching trending news",
    };
  }
};

/**
 * Fetch financial news events from API endpoint
 * @param {number} page
 * @param {number} limit
 * @returns {Promise<Object>} API response object
 */
export const fetchFinancialNewsEvents = async (page = 1, limit = 10) => {
  try {
    const res = await ApiClient.get(
      `${FEED_ENDPOINT}/financial-news/events/?page=${page}&limit=${limit}`,
    );

    if (res.ok) {
      const result = await res.json();
      return {
        success: true,
        data: result.data || [],
        pagination: result.pagination || {
          has_next: false,
          page: 1,
          total: 0,
        },
        status: res.status,
      };
    }
    return {
      success: false,
      message: "Failed to fetch financial news events",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error.message || "An error occurred fetching financial news events",
    };
  }
};

/**
 * Fetch a single financial news event details including associated news items
 * @param {string} eventUuid - UUID of the financial news event
 * @returns {Promise<Object>} Event detail object with nested 'news' array
 */
export const fetchFinancialNewsEventDetails = async (eventUuid) => {
  try {
    const res = await ApiClient.get(
      `${FEED_ENDPOINT}/financial-news/events/${eventUuid}/`,
    );

    if (res.ok) {
      const result = await res.json();
      return {
        success: true,
        data: result.data || {},
        status: res.status,
      };
    }
    return {
      success: false,
      message: "Failed to fetch event details",
      status: res.status,
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || "An error occurred fetching event details",
    };
  }
};
